import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  treasury,
  type TreasuryAccount,
  type TreasuryAccountCreate,
} from "@/api/funding";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

function errMsg(e: unknown) {
  return e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
}

/**
 * Treasury accounts — the org's real bank accounts. Segregated accounts
 * ring-fence client money from corporate funds (a regulatory requirement
 * for licensed brokers). Operators create/edit accounts here; balances are
 * posted by the funding + reconciliation flows.
 */
export function AdminTreasuryPage() {
  const list = useQuery({ queryKey: ["treasury"], queryFn: () => treasury.list() });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TreasuryAccount | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-term-fg">Treasury accounts</h1>
          <p className="text-xs text-term-mute">
            Bank accounts holding client and corporate funds. Segregated accounts ring-fence client money.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>New account</Button>
      </div>

      {list.isLoading ? (
        <div className="p-3 text-sm text-term-mute">Loading…</div>
      ) : !list.data || list.data.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              title="No treasury accounts"
              description="Add a segregated or corporate bank account to start tracking balances."
              action={<Button onClick={() => setCreating(true)}>Add first account</Button>}
            />
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {list.data.map((a) => (
            <Card key={a.id}>
              <CardBody className="space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-medium text-term-fg">{a.label}</div>
                    <div className="text-2xs text-term-dim">{a.bank_name ?? "—"}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge tone={a.is_segregated ? "good" : "neutral"}>
                      {a.is_segregated ? "Segregated" : "Corporate"}
                    </Badge>
                    <Badge tone={a.status === "active" ? "good" : "bad"}>{a.status}</Badge>
                  </div>
                </div>
                <div className="space-y-0.5 text-2xs text-term-dim">
                  {a.iban && <div>IBAN {a.iban}</div>}
                  {a.bic && <div>BIC {a.bic}</div>}
                  {a.account_number && <div>Acct {a.account_number}</div>}
                </div>
                <div className="border-t border-term-border pt-2">
                  <div className="mb-1 text-2xs uppercase tracking-wider text-term-mute">Balances</div>
                  {a.balances.length === 0 ? (
                    <div className="text-2xs text-term-mute">No balance recorded</div>
                  ) : (
                    <div className="space-y-0.5">
                      {a.balances.map((b) => (
                        <div key={b.currency} className="flex justify-between tnum text-term-fg">
                          <span className="text-term-dim">{b.currency}</span>
                          <span>{b.balance}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end pt-1">
                  <Button variant="ghost" onClick={() => setEditing(a)}>
                    Edit
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {creating && <TreasuryModal onClose={() => setCreating(false)} />}
      {editing && <TreasuryModal account={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function TreasuryModal({ account, onClose }: { account?: TreasuryAccount; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<TreasuryAccountCreate>({
    label: account?.label ?? "",
    currency: account?.currency ?? "USD",
    is_segregated: account?.is_segregated ?? true,
    bank_name: account?.bank_name ?? "",
    iban: account?.iban ?? "",
    bic: account?.bic ?? "",
    account_number: account?.account_number ?? "",
  });
  const [status, setStatus] = useState(account?.status ?? "active");
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      account ? treasury.patch(account.id, { ...form, status }) : treasury.create(form),
    onSuccess: () => {
      toast.push(account ? "Account updated" : "Account created", "success");
      qc.invalidateQueries({ queryKey: ["treasury"] });
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title={account ? `Edit · ${account.label}` : "New treasury account"}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          save.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Label</Label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </div>
          <div>
            <Label>Currency</Label>
            <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.is_segregated}
              onChange={(e) => setForm({ ...form, is_segregated: e.target.checked })}
            />
            Segregated (client money)
          </label>
          {account && (
            <div>
              <Label>Status</Label>
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </Select>
            </div>
          )}
        </div>
        <div>
          <Label>Bank name</Label>
          <Input value={form.bank_name ?? ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label>IBAN</Label>
            <Input value={form.iban ?? ""} onChange={(e) => setForm({ ...form, iban: e.target.value })} />
          </div>
          <div>
            <Label>BIC</Label>
            <Input value={form.bic ?? ""} onChange={(e) => setForm({ ...form, bic: e.target.value })} />
          </div>
          <div>
            <Label>Account #</Label>
            <Input
              value={form.account_number ?? ""}
              onChange={(e) => setForm({ ...form, account_number: e.target.value })}
            />
          </div>
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            {account ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
